// Helpers de formato replicados del sistema original (ClienteModal / AltaClientes).

const CONNECTORS = ["de", "del", "las", "los", "la", "el", "y", "en", "con", "para", "por", "un", "una", "unos", "unas"];

export function formatText(id, value) {
  if (!value || typeof value !== "string") return value;
  if (id === "rfc") return value.toUpperCase();
  if (id === "email" || id === "entregaUbicacionMaps") return value.toLowerCase();

  let words = value.toLowerCase().split(" ");
  words = words.map((word, index) => {
    if (word.length > 0) {
      if (index > 0 && CONNECTORS.includes(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    }
    return word;
  });
  let formatted = words.join(" ");
  formatted = formatted.replace(/(^|\s)s\.?a\.?\s*d[eé]\s*c\.?v\.?(?=$|\s|[.,])/gi, "$1SA de CV");
  return formatted;
}

export function formatDays(id, value, prevValue = "") {
  const cleanVal = value.replace(/\s*d[íi]as?\s*/gi, "").trim();
  if (/^\d+$/.test(cleanVal)) {
    const num = parseInt(cleanVal, 10);
    const targetValue = num === 1 ? `${num} día` : `${num} días`;
    const prevDigits = prevValue.replace(/\D/g, "");
    const newDigits = value.replace(/\D/g, "");
    const isPureBackspace = prevValue.startsWith(value) && value.length < prevValue.length;
    if (newDigits !== prevDigits && !isPureBackspace) return targetValue;
    return value;
  }
  return formatText(id, value);
}

export function formatCurrencyInput(value) {
  if (!value) return "";
  let numericValue = value.replace(/[^0-9.]/g, "");
  const parts = numericValue.split(".");
  if (parts.length > 2) numericValue = parts[0] + "." + parts.slice(1).join("");
  if (!numericValue) return "";
  const [intPart, decPart] = numericValue.split(".");
  const hasDecimal = numericValue.includes(".");
  const formattedInt = new Intl.NumberFormat("en-US").format(parseInt(intPart || "0", 10));
  return hasDecimal ? `$${formattedInt}.${decPart.slice(0, 2)}` : `$${formattedInt}`;
}

export function parseCurrency(str) {
  if (!str) return null;
  const n = parseFloat(str.replace(/[^0-9.]/g, ""));
  return isNaN(n) ? null : n;
}

export const money = (v) =>
  `$${Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const IVA_RATE = 0.16;
export const conIVA = (v) => Number(v || 0) * (1 + IVA_RATE);

// Número a letras (español) para el Pagaré. Ej: 40370.52 -> "CUARENTA MIL TRESCIENTOS SETENTA 52/100 M.N."
const NL_UNIDADES = ["", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve", "diez", "once", "doce", "trece", "catorce", "quince", "dieciséis", "diecisiete", "dieciocho", "diecinueve", "veinte", "veintiuno", "veintidós", "veintitrés", "veinticuatro", "veinticinco", "veintiséis", "veintisiete", "veintiocho", "veintinueve"];
const NL_DECENAS = ["", "", "", "treinta", "cuarenta", "cincuenta", "sesenta", "setenta", "ochenta", "noventa"];
const NL_CENTENAS = ["", "ciento", "doscientos", "trescientos", "cuatrocientos", "quinientos", "seiscientos", "setecientos", "ochocientos", "novecientos"];

function nlSeccion(n) {
  if (n === 100) return "cien";
  const c = Math.floor(n / 100), d = Math.floor((n % 100) / 10), u = n % 10, resto = n % 100;
  let out = "";
  if (c > 0) out += NL_CENTENAS[c] + " ";
  if (resto <= 29) out += NL_UNIDADES[resto];
  else { out += NL_DECENAS[d]; if (u > 0) out += " y " + NL_UNIDADES[u]; }
  return out.trim();
}

function nlMiles(n) {
  if (n < 1000) return nlSeccion(n);
  const miles = Math.floor(n / 1000), resto = n % 1000;
  let out = miles === 1 ? "mil" : nlSeccion(miles) + " mil";
  if (resto > 0) out += " " + nlSeccion(resto);
  return out.trim();
}

function nlMillones(n) {
  if (n < 1000000) return nlMiles(n);
  const mill = Math.floor(n / 1000000), resto = n % 1000000;
  let out = mill === 1 ? "un millón" : nlMiles(mill) + " millones";
  if (resto > 0) out += " " + nlMiles(resto);
  return out.trim();
}

export function numeroALetras(num) {
  const n = Number(num || 0);
  const entero = Math.floor(n);
  const cents = Math.round((n - entero) * 100);
  const palabras = entero === 0 ? "cero" : nlMillones(entero);
  const cc = String(cents).padStart(2, "0");
  return `${palabras} ${cc}/100 M.N.`.toUpperCase();
}

// Identificador de precio del día: ID-ddmmaa-<abrevLista>-<precio por kilo (subtotal c/desc / kg)>
export function buildIdentificador({ fecha, subtotalConDescuento, totalKg, listaNombre }) {
  let dd, mm, yy;
  if (typeof fecha === "string" && /^\d{4}-\d{2}-\d{2}/.test(fecha)) {
    const [y, m, d] = fecha.slice(0, 10).split("-");
    dd = d; mm = m; yy = y.slice(2);
  } else {
    const d = fecha ? new Date(fecha) : new Date();
    dd = String(d.getDate()).padStart(2, "0");
    mm = String(d.getMonth() + 1).padStart(2, "0");
    yy = String(d.getFullYear()).slice(2);
  }
  const stop = new Set(["de", "la", "el", "los", "las", "y", "del"]);
  const abbr = (listaNombre || "").split(/\s+/).filter((w) => w && !stop.has(w.toLowerCase())).map((w) => w[0].toUpperCase()).join("") || "LP";
  const ppk = Number(totalKg) > 0 ? (Number(subtotalConDescuento) || 0) / Number(totalKg) : 0;
  return `ID-${dd}${mm}${yy}-${abbr}-${ppk.toFixed(2)}`;
}

export const DESCUENTO_LABELS = {
  lealtad: "Descuento de lealtad",
  pago_contado: "Descuento pago contado",
  volumen: "Descuento por volumen",
  especial: "Descuento especial",
};

// Fechas formato México: dd/mm/aa y dd/mm/aa h:mm a.m.
const pad2 = (n) => String(n).padStart(2, "0");
const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  const s = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  return isNaN(d) ? null : d;
};
export const fechaMX = (v, vacio = "-") => {
  const d = toDate(v);
  return d ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}` : (v ? String(v) : vacio);
};
export const fechaHoraMX = (v, vacio = "—") => {
  const d = toDate(v);
  if (!d) return v ? String(v) : vacio;
  const h = d.getHours();
  return `${fechaMX(d)} ${h % 12 || 12}:${pad2(d.getMinutes())} ${h < 12 ? "a.m." : "p.m."}`;
};
